import { Link } from 'react-router-dom'
import { useMemo } from 'react'
import { useServices, useStacks } from '@/lib/cluster'
import { useAlerts } from '@/lib/alerts'
import { useHealthChecks, type HealthCheck } from '@/lib/health-checks'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'

interface StackInfo {
  name: string
  services_count: number
  active_services: number
  gone_services: number
  classifications: Record<string, number>
  checks_up: number
  checks_down: number
  checks_auto_disabled: number
  open_alerts: number
  open_criticals: number
}

function summarize(
  stackName: string,
  services: ReturnType<typeof useServices>['data'],
  checksByService: Map<string, HealthCheck[]>,
  alerts: ReturnType<typeof useAlerts>['data'],
): StackInfo {
  const ss = (services ?? []).filter(
    (s) => s.labels['com.docker.stack.namespace'] === stackName,
  )
  const classifications: Record<string, number> = {}
  let active_services = 0
  let gone_services = 0
  let checks_up = 0
  let checks_down = 0
  let checks_auto_disabled = 0

  for (const s of ss) {
    if (s.status === 'active') active_services += 1
    else gone_services += 1
    const type = s.classification_override ?? s.classification_auto ?? 'unknown'
    classifications[type] = (classifications[type] ?? 0) + 1

    const checks = checksByService.get(s.id) ?? []
    for (const c of checks) {
      if (c.auto_disabled_at) { checks_auto_disabled += 1; continue }
      if (!c.enabled) continue
      if (c.last_status == null ||
          c.last_status < c.expect_min ||
          c.last_status > c.expect_max) {
        checks_down += 1
      } else {
        checks_up += 1
      }
    }
  }

  const serviceNames = new Set(ss.map((s) => s.name))
  const open_alerts = (alerts ?? []).filter(
    (a) => a.closed_at === null && a.entity_name && serviceNames.has(a.entity_name),
  )
  const open_criticals = open_alerts.filter((a) => a.severity === 'critical').length

  return {
    name: stackName,
    services_count: ss.length,
    active_services,
    gone_services,
    classifications,
    checks_up,
    checks_down,
    checks_auto_disabled,
    open_alerts: open_alerts.length,
    open_criticals,
  }
}

export function StacksPage() {
  const { data: stacks, isLoading, error } = useStacks()
  const { data: services } = useServices()
  const { data: checks } = useHealthChecks()
  const { data: alerts } = useAlerts(false)

  const checksByService = useMemo(() => {
    const m = new Map<string, HealthCheck[]>()
    for (const c of checks ?? []) {
      const arr = m.get(c.service_id) ?? []
      arr.push(c)
      m.set(c.service_id, arr)
    }
    return m
  }, [checks])

  const infos = useMemo(
    () => (stacks ?? []).map((s) => summarize(s.name, services, checksByService, alerts)),
    [stacks, services, checksByService, alerts],
  )

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Stacks</h1>
        <p className="text-muted-foreground">Docker Compose stacks deployed on the Swarm.</p>
      </div>

      {error && <p className="text-sm text-destructive">Failed to load stacks.</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading &&
          Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-40" />)}
        {infos.map((info) => (
          <Link
            key={info.name}
            to={`/stacks/${encodeURIComponent(info.name)}`}
            className="block"
          >
            <Card className="h-full hover:border-primary/50 transition cursor-pointer">
              <CardHeader className="flex-row items-center justify-between pb-2">
                <CardTitle className="text-base">{info.name}</CardTitle>
                {info.open_criticals > 0 ? (
                  <Badge variant="outline" className="border-destructive/50 text-destructive">
                    {info.open_criticals} critical
                  </Badge>
                ) : info.open_alerts > 0 ? (
                  <Badge variant="outline" className="border-amber-500/50 text-amber-500">
                    {info.open_alerts} alert{info.open_alerts !== 1 ? 's' : ''}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-emerald-500/40 text-emerald-500">
                    OK
                  </Badge>
                )}
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <div className="text-muted-foreground">
                  {info.services_count} service{info.services_count !== 1 ? 's' : ''}
                  {info.gone_services > 0 && (
                    <span className="text-destructive"> · {info.gone_services} gone</span>
                  )}
                </div>
                {(info.checks_up + info.checks_down + info.checks_auto_disabled) > 0 && (
                  <div className="flex flex-wrap gap-1 text-xs">
                    {info.checks_up > 0 && (
                      <Badge variant="outline" className="border-emerald-500/40 text-emerald-500 text-[10px]">
                        {info.checks_up} up
                      </Badge>
                    )}
                    {info.checks_down > 0 && (
                      <Badge variant="outline" className="border-destructive/50 text-destructive text-[10px]">
                        {info.checks_down} down
                      </Badge>
                    )}
                    {info.checks_auto_disabled > 0 && (
                      <Badge variant="outline" className="border-amber-500/40 text-amber-500 text-[10px]">
                        {info.checks_auto_disabled} auto-disabled
                      </Badge>
                    )}
                  </div>
                )}
                {Object.keys(info.classifications).length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {Object.entries(info.classifications).map(([type, n]) => (
                      <Badge key={type} variant="secondary" className="text-[10px]">
                        {n} {type}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  )
}
